import {extractYouTubeId} from "../../utils/youtube";
import {
    createDefaultSongLibraryGuideState,
    SongLibraryGuideState,
} from "./song_library_guide";
import {
    ChoirsOverviewGuideState,
    createDefaultChoirsOverviewGuideState,
} from "./choirs_overview_guide";
import {
    ChoirRehearsalsGuideState,
    createDefaultChoirRehearsalsGuideState,
} from "./choir_rehearsals_guide";
import {
    ChoirSetlistsGuideState,
    createDefaultChoirSetlistsGuideState,
} from "./choir_setlists_guide";
import {
    ChoirScheduleGuideState,
    createDefaultChoirScheduleGuideState,
} from "./choir_schedule_guide";
import {
    ChoirMembersGuideState,
    createDefaultChoirMembersGuideState,
} from "./choir_members_guide";
import {
    ChoirRehearsalSongsGuideState,
    createDefaultChoirRehearsalSongsGuideState,
} from "./choir_rehearsal_songs_guide";
import {
    ChoirRehearsalRunthroughGuideState,
    createDefaultChoirRehearsalRunthroughGuideState,
} from "./choir_rehearsal_runthrough_guide";

export type ModuleHelpKey =
    | "song_library"
    | "community_challenges"
    | "community_reels"
    | "community_contributions"
    | "community_requests"
    | "marketplace_discover"
    | "marketplace_song_detail"
    | "marketplace_checkout"
    | "marketplace_orders"
    | "marketplace_purchases"
    | "marketplace_listings"
    | "marketplace_earnings"
    | "choirs_overview"
    | "choir_rehearsals"
    | "choir_rehearsal_songs"
    | "choir_setlists"
    | "choir_schedule"
    | "choir_members"
    | "choir_feeds"
    | "choir_integrations"
    | "choir_rehearsal_runthrough"
    | "projects_discover"
    | "projects_mine"
    | "projects_invites"
    | "projects_applications"
    | "projects_create"
    | "project_overview"
    | "project_applications"
    | "project_members"
    | "project_budget"
    | "project_agreements"
    | "project_invites"
    | "request_songs"
    | "rehearsals"
    | "vocal_contributions"
    | "setlists"
    | "uploads"
    | "account_information"
    | "profile"
    | "request_role"
    | "notifications"
    | "transaction_history";

export interface ModuleHelpEntry {
    first_seen_at?: string | null;
    hint_seen_at?: string | null;
    video_opened_at?: string | null;
    song_library_guide?: SongLibraryGuideState;
    choirs_overview_guide?: ChoirsOverviewGuideState;
    choir_rehearsals_guide?: ChoirRehearsalsGuideState;
    choir_rehearsal_songs_guide?: ChoirRehearsalSongsGuideState;
    choir_setlists_guide?: ChoirSetlistsGuideState;
    choir_schedule_guide?: ChoirScheduleGuideState;
    choir_members_guide?: ChoirMembersGuideState;
    choir_rehearsal_runthrough_guide?: ChoirRehearsalRunthroughGuideState;
}

export interface ModuleHelpPreferencesPayload {
    version: number;
    modules: Partial<Record<ModuleHelpKey, ModuleHelpEntry>>;
}

export interface ModuleHelpVideoSources {
    adaptive_low_latency?: {hls?: string | null; dash?: string | null};
    adaptive?: {hls?: string | null; dash?: string | null};
    web?: {hls?: string | null; dash?: string | null; mp4?: string | null; webm?: string | null};
    mobile?: {hls?: string | null; dash?: string | null; mp4?: string | null; webm?: string | null};
    youtube?: string | null;
}

export interface ModuleHelpSelectedPlayback {
    url?: string | null;
    kind?: string | null;
    format?: string | null;
    transport?: string | null;
    low_latency?: boolean;
}

export interface ModuleHelpVideoEntry {
    title?: string | null;
    poster_url?: string | null;
    thumbnail_url?: string | null;
    sources?: ModuleHelpVideoSources | null;
    selected_playback?: ModuleHelpSelectedPlayback | null;
}

export interface ModuleHelpVideoConfig {
    version?: number;
    modules?: Partial<Record<ModuleHelpKey, ModuleHelpVideoEntry>> & Record<string, ModuleHelpVideoEntry | undefined>;
}

export interface ResolvedModuleHelpVideo {
    mode: "embed" | "media" | "none";
    playbackUrl: string | null;
    rawUrl: string | null;
    posterUrl: string | null;
    format: string | null;
    lowLatency: boolean;
}

export interface ModuleHelpNextStep {
    route: string;
}

export interface ModuleHelpGuidance {
    currentStepLabelKey: string;
    nextStepLabelKey: string;
    bodyCopyKey: string;
    ctaLabelKey: string;
    nextRoute: string | null;
}

export const MODULE_HELP_PREFERENCES_VERSION = 1;

const CHOIR_MODULE_HELP_KEYS = [
    "choirs_overview",
    "choir_rehearsals",
    "choir_rehearsal_songs",
    "choir_setlists",
    "choir_schedule",
    "choir_members",
    "choir_integrations",
    "choir_rehearsal_runthrough",
] as const;

type ChoirModuleHelpKey = (typeof CHOIR_MODULE_HELP_KEYS)[number];
const CHOIR_MODULE_HELP_KEY_SET = new Set<ModuleHelpKey>(CHOIR_MODULE_HELP_KEYS);

const CHOIR_GUIDANCE_META: Record<ChoirModuleHelpKey, Omit<ModuleHelpGuidance, "nextRoute">> = {
    choirs_overview: {
        currentStepLabelKey: "components.header.moduleHelp.whatToDoNext.steps.choirs_overview",
        nextStepLabelKey: "components.header.moduleHelp.whatToDoNext.steps.choir_rehearsals",
        bodyCopyKey: "components.header.moduleHelp.whatToDoNext.copy.choirs_overview",
        ctaLabelKey: "components.header.moduleHelp.whatToDoNext.cta.choirs_overview",
    },
    choir_rehearsals: {
        currentStepLabelKey: "components.header.moduleHelp.whatToDoNext.steps.choir_rehearsals",
        nextStepLabelKey: "components.header.moduleHelp.whatToDoNext.steps.choir_setlists",
        bodyCopyKey: "components.header.moduleHelp.whatToDoNext.copy.choir_rehearsals",
        ctaLabelKey: "components.header.moduleHelp.whatToDoNext.cta.choir_rehearsals",
    },
    choir_rehearsal_songs: {
        currentStepLabelKey: "components.header.moduleHelp.whatToDoNext.steps.choir_rehearsal_songs",
        nextStepLabelKey: "components.header.moduleHelp.whatToDoNext.steps.choir_rehearsal_runthrough",
        bodyCopyKey: "components.header.moduleHelp.whatToDoNext.copy.choir_rehearsal_songs",
        ctaLabelKey: "components.header.moduleHelp.whatToDoNext.cta.choir_rehearsal_songs",
    },
    choir_setlists: {
        currentStepLabelKey: "components.header.moduleHelp.whatToDoNext.steps.choir_setlists",
        nextStepLabelKey: "components.header.moduleHelp.whatToDoNext.steps.choir_schedule",
        bodyCopyKey: "components.header.moduleHelp.whatToDoNext.copy.choir_setlists",
        ctaLabelKey: "components.header.moduleHelp.whatToDoNext.cta.choir_setlists",
    },
    choir_schedule: {
        currentStepLabelKey: "components.header.moduleHelp.whatToDoNext.steps.choir_schedule",
        nextStepLabelKey: "components.header.moduleHelp.whatToDoNext.steps.choir_members",
        bodyCopyKey: "components.header.moduleHelp.whatToDoNext.copy.choir_schedule",
        ctaLabelKey: "components.header.moduleHelp.whatToDoNext.cta.choir_schedule",
    },
    choir_members: {
        currentStepLabelKey: "components.header.moduleHelp.whatToDoNext.steps.choir_members",
        nextStepLabelKey: "components.header.moduleHelp.whatToDoNext.steps.choir_integrations",
        bodyCopyKey: "components.header.moduleHelp.whatToDoNext.copy.choir_members",
        ctaLabelKey: "components.header.moduleHelp.whatToDoNext.cta.choir_members",
    },
    choir_integrations: {
        currentStepLabelKey: "components.header.moduleHelp.whatToDoNext.steps.choir_integrations",
        nextStepLabelKey: "components.header.moduleHelp.whatToDoNext.steps.choir_rehearsals",
        bodyCopyKey: "components.header.moduleHelp.whatToDoNext.copy.choir_integrations",
        ctaLabelKey: "components.header.moduleHelp.whatToDoNext.cta.choir_integrations",
    },
    choir_rehearsal_runthrough: {
        currentStepLabelKey: "components.header.moduleHelp.whatToDoNext.steps.choir_rehearsal_runthrough",
        nextStepLabelKey: "components.header.moduleHelp.whatToDoNext.steps.choir_rehearsal_review",
        bodyCopyKey: "components.header.moduleHelp.whatToDoNext.copy.choir_rehearsal_runthrough",
        ctaLabelKey: "components.header.moduleHelp.whatToDoNext.cta.choir_rehearsal_runthrough",
    },
};

function isChoirModuleHelpKey(moduleKey: ModuleHelpKey): moduleKey is ChoirModuleHelpKey {
    return CHOIR_MODULE_HELP_KEY_SET.has(moduleKey);
}

const MODULE_HELP_ROUTE_PATTERNS: Array<{key: ModuleHelpKey; pattern: RegExp}> = [
    {key: "community_challenges", pattern: /^\/community\/challenges(?:\/|$)/i},
    {key: "community_reels", pattern: /^\/community\/reels(?:\/|$)/i},
    {key: "community_contributions", pattern: /^\/community\/contributions(?:\/|$)/i},
    {key: "community_requests", pattern: /^\/community\/requests(?:\/|$)/i},
    {key: "community_contributions", pattern: /^\/community-contributions(?:\/|$)/i},
    {key: "community_requests", pattern: /^\/community-contribution-requests(?:\/|$)/i},

    {key: "marketplace_checkout", pattern: /^\/marketplace\/checkout(?:\/|$)/i},
    {key: "marketplace_orders", pattern: /^\/marketplace\/orders(?:\/|$)/i},
    {key: "marketplace_purchases", pattern: /^\/marketplace\/purchases(?:\/|$)/i},
    {key: "marketplace_listings", pattern: /^\/marketplace\/listings(?:\/|$)/i},
    {key: "marketplace_earnings", pattern: /^\/marketplace\/earnings(?:\/|$)/i},
    {key: "marketplace_song_detail", pattern: /^\/marketplace\/[^/]+(?:\/|$)/i},
    {key: "marketplace_discover", pattern: /^\/marketplace(?:\/|$)/i},

    {key: "choir_rehearsal_runthrough", pattern: /^\/choirs\/[^/]+\/rehearsal\/[^/]+\/[^/]+(?:\/|$)/i},
    {key: "choir_rehearsal_songs", pattern: /^\/choirs\/[^/]+\/rehearsal\/[^/]+(?:\/|$)/i},
    {key: "choir_setlists", pattern: /^\/choirs\/[^/]+\/setlists(?:\/|$)/i},
    {key: "choir_schedule", pattern: /^\/choirs\/[^/]+\/schedule(?:\/|$)/i},
    {key: "choir_members", pattern: /^\/choirs\/[^/]+\/members(?:\/|$)/i},
    {key: "choir_feeds", pattern: /^\/choirs\/[^/]+\/feeds(?:\/|$)/i},
    {key: "choir_integrations", pattern: /^\/choirs\/[^/]+\/integrate(?:\/|$)/i},
    {key: "choir_rehearsals", pattern: /^\/choirs\/[^/]+(?:\/rehearsals)?(?:\/|$)/i},
    {key: "choirs_overview", pattern: /^\/choirs(?:\/|$)/i},

    {key: "projects_create", pattern: /^\/projects\/create(?:\/|$)/i},
    {key: "projects_mine", pattern: /^\/projects\/mine(?:\/|$)/i},
    {key: "projects_invites", pattern: /^\/projects\/invites(?:\/|$)/i},
    {key: "projects_applications", pattern: /^\/projects\/applications(?:\/|$)/i},
    {key: "project_applications", pattern: /^\/projects\/[^/]+\/applications(?:\/|$)/i},
    {key: "project_members", pattern: /^\/projects\/[^/]+\/members(?:\/|$)/i},
    {key: "project_budget", pattern: /^\/projects\/[^/]+\/budget(?:\/|$)/i},
    {key: "project_agreements", pattern: /^\/projects\/[^/]+\/agreements(?:\/|$)/i},
    {key: "project_invites", pattern: /^\/projects\/[^/]+\/invites(?:\/|$)/i},
    {key: "project_overview", pattern: /^\/projects\/[^/]+(?:\/|$)/i},
    {key: "projects_discover", pattern: /^\/projects(?:\/|$)/i},

    {key: "song_library", pattern: /^\/home(?:\/|$)/i},
    {key: "request_songs", pattern: /^\/request-songs(?:\/|$)/i},
    {key: "rehearsals", pattern: /^\/rehearsals(?:\/|$)/i},
    {key: "vocal_contributions", pattern: /^\/contributions(?:\/|$)/i},
    {key: "setlists", pattern: /^\/setlists(?:\/|$)/i},
    {key: "uploads", pattern: /^\/uploads(?:\/|$)/i},
    {key: "marketplace_orders", pattern: /^\/account-information\/orders(?:\/|$)/i},
    {key: "marketplace_purchases", pattern: /^\/account-information\/purchases(?:\/|$)/i},
    {key: "marketplace_listings", pattern: /^\/account-information\/listings(?:\/|$)/i},
    {key: "marketplace_earnings", pattern: /^\/account-information\/earnings(?:\/|$)/i},
    {key: "account_information", pattern: /^\/account-information(?:\/|$)/i},
    {key: "profile", pattern: /^\/profile(?:\/|$)/i},
    {key: "request_role", pattern: /^\/request-role(?:\/|$)/i},
    {key: "notifications", pattern: /^\/notifications(?:\/|$)/i},
    {key: "transaction_history", pattern: /^\/transaction-history(?:\/|$)/i},
];

export function resolveModuleHelpKey(pathname: string): ModuleHelpKey | null {
    if (!pathname || typeof pathname !== "string") {
        return null;
    }

    const matched = MODULE_HELP_ROUTE_PATTERNS.find(({pattern}) => pattern.test(pathname));
    return matched?.key ?? null;
}

export function getEmbedVideoUrl(videoUrl: string | null): string | null {
    if (!videoUrl) {
        return null;
    }

    const youtubeId = extractYouTubeId(videoUrl);
    if (youtubeId) {
        return `https://www.youtube-nocookie.com/embed/${youtubeId}`;
    }

    return videoUrl;
}

function normalizeNonEmptyUrl(value: string | null | undefined): string | null {
    if (typeof value !== "string") {
        return null;
    }

    const trimmed = value.trim();
    if (!trimmed.length) {
        return null;
    }
    const lowered = trimmed.toLowerCase();
    if (lowered === "null" || lowered === "undefined") {
        return null;
    }
    if (!/^https?:\/\//i.test(trimmed)) {
        return null;
    }
    return trimmed;
}

function inferPosterFromVideoUrl(videoUrl: string | null): string | null {
    if (!videoUrl) {
        return null;
    }

    try {
        const parsed = new URL(videoUrl);
        const segments = parsed.pathname.split("/");
        if (!segments.length) {
            return null;
        }
        segments[segments.length - 1] = "thumbnail.jpg";
        parsed.pathname = segments.join("/");
        return parsed.toString();
    } catch (_error) {
        return null;
    }
}

function resolvePosterUrl(
    moduleEntry: ModuleHelpVideoEntry | undefined,
    rawUrl: string | null
): string | null {
    const explicitPosterUrl = normalizeNonEmptyUrl(
        moduleEntry?.poster_url
        || moduleEntry?.thumbnail_url
        || null
    );
    if (explicitPosterUrl) {
        return explicitPosterUrl;
    }

    const sourceCandidates = [
        rawUrl,
        moduleEntry?.sources?.adaptive_low_latency?.hls || null,
        moduleEntry?.sources?.adaptive_low_latency?.dash || null,
        moduleEntry?.sources?.adaptive?.hls || null,
        moduleEntry?.sources?.adaptive?.dash || null,
        moduleEntry?.sources?.web?.hls || null,
        moduleEntry?.sources?.web?.dash || null,
        moduleEntry?.sources?.mobile?.hls || null,
        moduleEntry?.sources?.mobile?.dash || null,
        moduleEntry?.sources?.web?.mp4 || null,
        moduleEntry?.sources?.mobile?.mp4 || null,
    ];

    for (const sourceCandidate of sourceCandidates) {
        const inferredPoster = inferPosterFromVideoUrl(sourceCandidate);
        if (inferredPoster) {
            return inferredPoster;
        }
    }

    const youtubeUrl = rawUrl || moduleEntry?.sources?.youtube || "";
    const youtubeId = extractYouTubeId(youtubeUrl);
    if (youtubeId) {
        return `https://i.ytimg.com/vi/${youtubeId}/hqdefault.jpg`;
    }

    return null;
}

function chooseFallbackSource(
    sources: ModuleHelpVideoSources,
    prefersAppleStream: boolean
): ModuleHelpSelectedPlayback | null {
    const appleCandidates: ModuleHelpSelectedPlayback[] = [
        {url: sources.adaptive_low_latency?.hls, kind: "adaptive_low_latency", format: "hls", low_latency: true},
        {url: sources.adaptive?.hls, kind: "adaptive", format: "hls", low_latency: false},
        {url: sources.mobile?.hls, kind: "mobile", format: "hls", low_latency: false},
        {url: sources.web?.hls, kind: "web", format: "hls", low_latency: false},
        {url: sources.mobile?.mp4, kind: "mobile", format: "mp4", low_latency: false},
        {url: sources.web?.mp4, kind: "web", format: "mp4", low_latency: false},
        {url: sources.web?.webm, kind: "web", format: "webm", low_latency: false},
        {url: sources.adaptive_low_latency?.dash, kind: "adaptive_low_latency", format: "dash", low_latency: true},
        {url: sources.adaptive?.dash, kind: "adaptive", format: "dash", low_latency: false},
        {url: sources.mobile?.dash, kind: "mobile", format: "dash", low_latency: false},
        {url: sources.web?.dash, kind: "web", format: "dash", low_latency: false},
        {url: sources.youtube, kind: "youtube", format: "youtube", low_latency: false},
    ];

    const nonAppleCandidates: ModuleHelpSelectedPlayback[] = [
        {url: sources.adaptive_low_latency?.dash, kind: "adaptive_low_latency", format: "dash", low_latency: true},
        {url: sources.adaptive?.dash, kind: "adaptive", format: "dash", low_latency: false},
        {url: sources.web?.dash, kind: "web", format: "dash", low_latency: false},
        {url: sources.mobile?.dash, kind: "mobile", format: "dash", low_latency: false},
        {url: sources.web?.webm, kind: "web", format: "webm", low_latency: false},
        {url: sources.web?.mp4, kind: "web", format: "mp4", low_latency: false},
        {url: sources.mobile?.mp4, kind: "mobile", format: "mp4", low_latency: false},
        {url: sources.adaptive_low_latency?.hls, kind: "adaptive_low_latency", format: "hls", low_latency: true},
        {url: sources.adaptive?.hls, kind: "adaptive", format: "hls", low_latency: false},
        {url: sources.web?.hls, kind: "web", format: "hls", low_latency: false},
        {url: sources.mobile?.hls, kind: "mobile", format: "hls", low_latency: false},
        {url: sources.youtube, kind: "youtube", format: "youtube", low_latency: false},
    ];

    const candidates = prefersAppleStream ? appleCandidates : nonAppleCandidates;
    return candidates.find((candidate) => Boolean(candidate.url)) || null;
}

export function resolveModuleHelpVideo(
    moduleKey: ModuleHelpKey | null,
    moduleHelpVideoConfig?: ModuleHelpVideoConfig | null
): ResolvedModuleHelpVideo {
    if (!moduleKey) {
        return {
            mode: "none",
            playbackUrl: null,
            rawUrl: null,
            posterUrl: null,
            format: null,
            lowLatency: false,
        };
    }

    const moduleEntry = moduleHelpVideoConfig?.modules?.[moduleKey];
    const selected = moduleEntry?.selected_playback && moduleEntry.selected_playback.url
        ? moduleEntry.selected_playback
        : chooseFallbackSource(
            moduleEntry?.sources || {},
            typeof navigator !== "undefined"
            && (/iPhone|iPad|iPod/i.test(navigator.userAgent || "")
                || (/Safari/i.test(navigator.userAgent || "") && !/Chrome|Chromium|Edg|OPR/i.test(navigator.userAgent || "")))
        );

    const rawUrl = selected?.url || null;
    if (!rawUrl) {
        return {
            mode: "none",
            playbackUrl: null,
            rawUrl: null,
            posterUrl: null,
            format: null,
            lowLatency: false,
        };
    }

    const embedUrl = getEmbedVideoUrl(rawUrl);
    const isEmbed = Boolean(embedUrl && embedUrl !== rawUrl);

    return {
        mode: isEmbed ? "embed" : "media",
        playbackUrl: isEmbed ? embedUrl : rawUrl,
        rawUrl,
        posterUrl: resolvePosterUrl(moduleEntry, rawUrl),
        format: selected?.format || null,
        lowLatency: Boolean(selected?.low_latency),
    };
}

export function isPlayableModuleHelpVideo(video: ResolvedModuleHelpVideo | null | undefined): boolean {
    if (!video) {
        return false;
    }

    return video.mode === "media" || video.mode === "embed";
}

export function canRenderModuleHelpHint(
    moduleKey: ModuleHelpKey | null,
    moduleHelpLoaded: boolean,
    moduleHelpVideo: ResolvedModuleHelpVideo | null | undefined
): boolean {
    if (!moduleKey || !moduleHelpLoaded) {
        return false;
    }

    return isPlayableModuleHelpVideo(moduleHelpVideo);
}

export function createDefaultModuleHelpPreferences(): ModuleHelpPreferencesPayload {
    return {
        version: MODULE_HELP_PREFERENCES_VERSION,
        modules: {},
    };
}

export function getSongLibraryGuideFromModuleEntry(entry?: ModuleHelpEntry | null): SongLibraryGuideState {
    return createDefaultSongLibraryGuideState(entry?.song_library_guide || null);
}

export function getChoirsOverviewGuideFromModuleEntry(entry?: ModuleHelpEntry | null): ChoirsOverviewGuideState {
    return createDefaultChoirsOverviewGuideState(entry?.choirs_overview_guide || null);
}

export function getChoirRehearsalsGuideFromModuleEntry(entry?: ModuleHelpEntry | null): ChoirRehearsalsGuideState {
    return createDefaultChoirRehearsalsGuideState(entry?.choir_rehearsals_guide || null);
}

export function getChoirRehearsalSongsGuideFromModuleEntry(entry?: ModuleHelpEntry | null): ChoirRehearsalSongsGuideState {
    return createDefaultChoirRehearsalSongsGuideState(entry?.choir_rehearsal_songs_guide || null);
}

export function getChoirSetlistsGuideFromModuleEntry(entry?: ModuleHelpEntry | null): ChoirSetlistsGuideState {
    return createDefaultChoirSetlistsGuideState(entry?.choir_setlists_guide || null);
}

export function getChoirScheduleGuideFromModuleEntry(entry?: ModuleHelpEntry | null): ChoirScheduleGuideState {
    return createDefaultChoirScheduleGuideState(entry?.choir_schedule_guide || null);
}

export function getChoirMembersGuideFromModuleEntry(entry?: ModuleHelpEntry | null): ChoirMembersGuideState {
    return createDefaultChoirMembersGuideState(entry?.choir_members_guide || null);
}

export function getChoirRehearsalRunthroughGuideFromModuleEntry(entry?: ModuleHelpEntry | null): ChoirRehearsalRunthroughGuideState {
    return createDefaultChoirRehearsalRunthroughGuideState(entry?.choir_rehearsal_runthrough_guide || null);
}

function extractChoirContext(pathname: string) {
    const choirMatch = pathname.match(/^\/choirs\/([^/]+)/i);
    const rehearsalSongsMatch = pathname.match(/^\/choirs\/([^/]+)\/rehearsal\/([^/]+)(?:\/|$)/i);
    const rehearsalMatch = pathname.match(/^\/choirs\/([^/]+)\/rehearsal\/([^/]+)\/([^/]+)/i);

    return {
        choirId: choirMatch?.[1] || null,
        rehearsalId: rehearsalMatch?.[2] || rehearsalSongsMatch?.[2] || null,
        songId: rehearsalMatch?.[3] || null,
    };
}

function extractProjectContext(pathname: string) {
    const projectMatch = pathname.match(/^\/projects\/([^/]+)/i);
    return {
        projectId: projectMatch?.[1] || null,
    };
}

export function resolveModuleHelpNextStep(
    moduleKey: ModuleHelpKey | null,
    pathname: string
): ModuleHelpNextStep | null {
    if (!moduleKey || !pathname) {
        return null;
    }

    const choirContext = extractChoirContext(pathname);
    const projectContext = extractProjectContext(pathname);

    switch (moduleKey) {
        case "community_challenges":
            return {route: "/community/challenges"};
        case "community_reels":
            return {route: "/community/reels"};
        case "community_contributions":
            return {route: "/community/contributions"};
        case "community_requests":
            return {route: "/community/requests"};

        case "choirs_overview":
            return {route: "/choirs/create"};
        case "choir_rehearsals":
            return choirContext.choirId ? {route: `/choirs/${choirContext.choirId}/setlists`} : null;
        case "choir_rehearsal_songs":
            return choirContext.choirId && choirContext.rehearsalId
                ? {route: `/choirs/${choirContext.choirId}/rehearsal/${choirContext.rehearsalId}`}
                : null;
        case "choir_setlists":
            return choirContext.choirId ? {route: `/choirs/${choirContext.choirId}/schedule`} : null;
        case "choir_schedule":
            return choirContext.choirId ? {route: `/choirs/${choirContext.choirId}/members`} : null;
        case "choir_members":
            return choirContext.choirId ? {route: `/choirs/${choirContext.choirId}/integrate`} : null;
        case "choir_integrations":
            return choirContext.choirId ? {route: `/choirs/${choirContext.choirId}/rehearsals`} : null;
        case "choir_rehearsal_runthrough":
            return choirContext.choirId && choirContext.rehearsalId && choirContext.songId
                ? {route: `/choirs/${choirContext.choirId}/rehearsal/${choirContext.rehearsalId}/${choirContext.songId}/review`}
                : null;

        case "projects_discover":
            return {route: "/projects/create"};
        case "projects_create":
            return {route: "/projects/mine"};
        case "projects_mine":
            return {route: "/projects/invites"};
        case "projects_invites":
            return {route: "/projects/applications"};
        case "project_overview":
            return projectContext.projectId ? {route: `/projects/${projectContext.projectId}/applications`} : null;
        case "project_applications":
            return projectContext.projectId ? {route: `/projects/${projectContext.projectId}/members`} : null;
        case "project_members":
            return projectContext.projectId ? {route: `/projects/${projectContext.projectId}/budget`} : null;
        case "project_budget":
            return projectContext.projectId ? {route: `/projects/${projectContext.projectId}/agreements`} : null;
        case "project_agreements":
            return projectContext.projectId ? {route: `/projects/${projectContext.projectId}/invites`} : null;
        case "uploads":
            return {route: "/uploads"};
        default:
            return null;
    }
}

export function resolveChoirModuleHelpGuidance(
    moduleKey: ModuleHelpKey | null,
    pathname: string
): ModuleHelpGuidance | null {
    if (!moduleKey || !isChoirModuleHelpKey(moduleKey)) {
        return null;
    }

    const moduleMeta = CHOIR_GUIDANCE_META[moduleKey];
    const nextStep = resolveModuleHelpNextStep(moduleKey, pathname);

    return {
        ...moduleMeta,
        nextRoute: nextStep?.route || null,
    };
}
